import { CallIntelligenceWritebackService } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/writeback/v1/writeback_service.pb';
import { ActiveStatus, PetsAPI } from '@weave/schema-gen-ts/dist/schemas/pets-api/v1/pets_api.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { DemoInterceptorUtils, FeatureMockRouter } from '@frontend/demo-interceptors';
import { MockAPIRouter } from '@frontend/fetch';
import { serviceName } from '../../writeback/v1/service';
import {
  CallWritebackStatusV2IO,
  GetWritebackCapabilitiesIO,
  WritebackCallsIO,
  WritebackStatus,
} from '../../writeback/v1/types';
import { BaseMockRouterContext } from './types';

export type CallIntelPmsSyncMockRouterContext = BaseMockRouterContext;

const DEMO_PETS = [
  { name: 'Buddy', petPmDisplayId: 'demo-pet-pms-1' },
  { name: 'Luna', petPmDisplayId: 'demo-pet-pms-2' },
  { name: 'Charlie', petPmDisplayId: 'demo-pet-pms-3' },
];

// In-memory map to track which serviceablePmsIds have been synced per call in demo mode
const CallIDPmsSyncMap: Record<string, Set<string>> = {};

export const CallIntelPmsSyncMockRouter: FeatureMockRouter<CallIntelPmsSyncMockRouterContext> = {
  addMockRouterEndpoints: (router, context) => {
    const isVetVertical = context.vertical === Vertical.VET;

    router.schemaEndpoint(CallIntelligenceWritebackService.CallWritebackStatusV2, async (request, params, next) => {
      const { searchParams } = new URL(request.url);
      const isDemo = searchParams.get('isDemo') ?? '';
      if (isDemo && params.callId) {
        const syncedIds = CallIDPmsSyncMap[params.callId] ?? new Set<string>();

        if (isVetVertical) {
          const mockResponse: CallWritebackStatusV2IO['output'] = {
            callWritebacks: DEMO_PETS.map((pet) => {
              const isSynced = syncedIds.has(pet.petPmDisplayId);
              return {
                callId: params.callId,
                serviceablePmsId: pet.petPmDisplayId,
                status: isSynced
                  ? WritebackStatus.WRITEBACK_STATUS_SUCCEEDED
                  : WritebackStatus.WRITEBACK_STATUS_READY_TO_SYNC,
                lastSyncedAt: isSynced ? new Date().toISOString() : undefined,
              };
            }),
          };
          return MockAPIRouter.json(mockResponse);
        }

        const isSynced = syncedIds.has('');
        const mockResponse: CallWritebackStatusV2IO['output'] = {
          callWritebacks: [
            {
              callId: params.callId,
              status: isSynced
                ? WritebackStatus.WRITEBACK_STATUS_SUCCEEDED
                : WritebackStatus.WRITEBACK_STATUS_READY_TO_SYNC,
              lastSyncedAt: isSynced ? new Date().toISOString() : undefined,
            },
          ],
        };
        return MockAPIRouter.json(mockResponse);
      }

      next();
      return null;
    });

    if (isVetVertical) {
      router.schemaEndpoint(PetsAPI.FetchPetsByPersonId, async (request, _params, next) => {
        const { searchParams } = new URL(request.url);
        const isDemo = searchParams.get('isDemo') ?? '';
        if (isDemo) {
          return MockAPIRouter.json({
            pets: DEMO_PETS.map((pet) => ({
              ...pet,
              activeStatus: ActiveStatus.ACTIVE,
            })),
          });
        }

        next();
        return null;
      });
    }
  },
  getRequestInterceptors: ({ interceptorIdPrefix, vertical, hasVoiceAiAccess }) => [
    {
      id: `${interceptorIdPrefix}-writeback-capability`,
      interceptor: async (request: Request) => {
        const isMatching = await DemoInterceptorUtils.isMatchingApiEndpoint({
          schemaMethod: CallIntelligenceWritebackService.GetWritebackCapabilities,
          requestUrl: request.url,
          method: request.method,
        });

        if (!isMatching) {
          return request;
        }

        const requestBody: GetWritebackCapabilitiesIO['input'] = await request.clone().json();

        if (requestBody.isDemo) {
          requestBody.demoMetadata = {
            ...requestBody.demoMetadata,
            vertical,
            features: {
              enableCallIntelligence: true,
              enableVoiceAi: hasVoiceAiAccess,
            },
          };
        }

        return new Request(request, {
          body: JSON.stringify(requestBody),
        });
      },
    },
    {
      id: `${interceptorIdPrefix}-manual-sync`,
      interceptor: async (request) => {
        const isMatching = await DemoInterceptorUtils.isMatchingApiEndpoint({
          schemaMethod: CallIntelligenceWritebackService.WritebackCalls,
          requestUrl: request.url,
          method: request.method,
        });

        if (!isMatching) {
          return request;
        }

        const requestBody: WritebackCallsIO['input'] = await request.clone().json();

        if (requestBody.isDemo) {
          if (!CallIDPmsSyncMap[requestBody.callId]) {
            CallIDPmsSyncMap[requestBody.callId] = new Set<string>();
          }
          CallIDPmsSyncMap[requestBody.callId]?.add(requestBody.serviceablePmsId || '');
          requestBody.demoMetadata = {
            ...requestBody.demoMetadata,
            vertical,
            features: {
              enableCallIntelligence: true,
              enableVoiceAi: hasVoiceAiAccess,
            },
          };
        }

        return new Request(request, {
          body: JSON.stringify(requestBody),
        });
      },
    },
  ],
  invalidateQueries: (queryClient) => {
    queryClient.invalidateQueries([serviceName, 'GetWritebackCapabilities']);
  },
};
